<template>
  <div v-if="isLoading">
    <LoadingSpinner />
  </div>
  <div v-else>
    <section class="mt m-auto pa-desktop constrain-90">
      <button class="btn-link left-link mb" @click="cancel()">
        <svg-icon-component icon="chevron-left" class="icon-sm" />
        {{ t("EditShipping.back") }}
      </button>
      <h1 class="opposite-ends mb-double">
        <span class="h1">{{ t("BuyPaymentComponent.payment") }}</span>
      </h1>
      <!-- -->
      <ul v-if="FLAGS.ENABLE_BUYFLOW_PAYPAL === 'true'" class="flex-start-md choose-cc-or-pp mb-double">
        <li>
          <label class="flex-start pa-half" :class="paymentType === paymentTypes.creditCard ? 'selected' : ''">
            <input
              id="payment-type-cc"
              v-model="paymentType"
              type="radio"
              name="paymentType"
              value="creditCard"
              class="mr-quarter"
              @click="setPaymentType(paymentTypes.creditCard)"
            />
            <svg-icon-component icon="credit-card" class="mr-quarter" />
            {{ t("BuyPaymentComponent.paymentTypeCC") }}
          </label>
        </li>
        <li>
          <label class="flex-start pa-half" :class="paymentType === paymentTypes.paypal ? 'selected' : ''">
            <input
              id="payment-type-pp"
              v-model="paymentType"
              type="radio"
              name="paymentType"
              value="paypal"
              class="mr-quarter"
              @click="setPaymentType(paymentTypes.paypal)"
            />
            <svg-icon-component icon="paypal-icon" class="mr-quarter" />
            {{ t("BuyPaymentComponent.paymentTypePP") }}
          </label>
        </li>
      </ul>
      <section v-show="paymentType === paymentTypes.creditCard">
        <section v-if="displayCreditCardDropDown" id="credit-card-drop-down" class="constrain-65">
          <SelectPayment
            :credit-cards-prop="buyFlowStore.creditCards"
            @add-card="addCard"
            @set-display-card-details-state="displaySelectedCardDetails"
          />
        </section>
        <section
          v-if="displayAddCreditCard && FLAGS.ENABLE_BUYFLOW_REDUCED_FIELD === 'false'"
          id="credit-card-add-section"
        >
          <AddPaymentMethodComponent
            :show-submit-btn="false"
            :form-submit-button-text="t('Common.reviewOrder')"
            :enable-captcha="false"
            :user-guid="buyFlowStore.userGuid"
            :email="email"
            :computer-id="computerId"
            :pre-populate-form="paymentPageStore.creditAddress"
            @payment-method-added="editPaymentCreditCardSuccessHandler"
            @payment-method-error="editPaymentCreditCardFailureHandler"
          />
        </section>
        <section
          v-if="displayAddCreditCard && FLAGS.ENABLE_BUYFLOW_REDUCED_FIELD === 'true'"
          id="credit-card-add-section-reduced-field"
        >
          <AddPaymentReducedFields
            :show-submit-btn="false"
            :form-submit-button-text="t('Common.reviewOrder')"
            :enable-captcha="false"
            :user-guid="buyFlowStore.userGuid"
            :email="email"
            :computer-id="computerId"
            :pre-populate-form="paymentPageStore.creditAddress"
            @payment-method-added="editPaymentCreditCardSuccessHandler"
            @payment-method-error="editPaymentCreditCardFailureHandler"
          />
        </section>
      </section>
      <div v-if="paymentType === paymentTypes.paypal" class="mb-double">
        <button class="btn-shell" @click="initiatePayPalRequest()">
          <svg-icon-component icon="paypal-button" class="paypal-svg" />
        </button>
      </div>
      <ul class="flex-start-md mb-double">
        <li>
          <button id="btnCancel" type="button" class="btn-outline-primary mr" @click="cancel()">
            {{ t("Common.cancel") }}
          </button>
        </li>
        <li>
          <button id="btnSubmit" type="button" class="btn-primary" @click="submit()">{{ tn("savePayment") }}</button>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { t } from "@/i18n";
import { FLAGS } from "@/define";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import { useUserStore } from "@/stores/user";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { changePageState, loadCreditCards } from "@/components/Buy/BuyHelper";
import { layoutStates, pageStates } from "@/components/Buy/BuyEnums";
import { logEvent, logException } from "@/common/logger";
import { IPayPalAgreementTokenResponse } from "@/common/api/unifiedPortal/interfaces";
import { unifiedApi } from "@/common";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import SelectPayment from "@/components/Buy/Payment/SelectPayment.vue";
import { usePaymentPageStore } from "@/stores/paymentPage";
import AddPaymentMethodComponent from "@/components/shared/AddPaymentMethodComponent.vue";
import AddPaymentReducedFields from "@/components/shared/AddPaymentReducedFields.vue";
import { NotificationType } from "@/stores/notificationsInterfaces";
import { useNotificationsStore } from "@/stores/notifications";

enum editPaymentDisplayStates {
  displayCreditCards = "displayCreditCards",
  addCreditCard = "addCreditCard",
}
enum paymentTypes {
  paypal = "paypal",
  creditCard = "creditCard",
}
const editPaymentDisplayState = ref(editPaymentDisplayStates.displayCreditCards);
const paymentType = ref(paymentTypes.creditCard);
const componentName = "EditPayment";
const buyFlowStore = useBuyFlowStore();
const isLoading = ref(false);
const paymentPageStore = usePaymentPageStore();
const userStore = useUserStore();
const computerId = computed(() => buyFlowStore.cart?.items[0]?.computerId ?? 0);
const email = computed(() => userStore.currentUser?.email ?? userStore.email);
const notificationsStore = useNotificationsStore();

const displayAddCreditCard = computed(() => {
  return editPaymentDisplayState.value === editPaymentDisplayStates.addCreditCard;
});

const displayCreditCardDropDown = computed(() => {
  return (
    userStore.isEstablishedAccount && editPaymentDisplayState.value === editPaymentDisplayStates.displayCreditCards
  );
});

onBeforeMount(() => {
  loadPage();
});

function addCard() {
  editPaymentDisplayState.value = editPaymentDisplayStates.addCreditCard;
}

function displaySelectedCardDetails() {
  editPaymentDisplayState.value = editPaymentDisplayStates.displayCreditCards;
}

async function setNewDefaultCC(creditCardId: string) {
  try {
    if (userStore.currentUser) {
      await unifiedApi.setDefaultCreditCard(creditCardId);
    }
  } catch (err) {
    handleApiError(err as AxiosError);
  }
}

async function editPaymentCreditCardSuccessHandler(event) {
  try {
    paymentPageStore.creditAddress = {
      creditCardAddress1: event.detail.creditCardAddress1?.replaceAll("+", " ") || "",
      creditCardAddress2: event.detail.creditCardAddress2?.replaceAll("+", " ") || "",
      creditCardPostalCode: event.detail.creditCardPostalCode?.replaceAll("+", " ") || "",
      creditCardCity: event.detail.creditCardCity?.replaceAll("+", " ") || "",
      creditCardCountry: event.detail.creditCardCountry || "",
      creditCardState: event.detail.creditCardState?.replaceAll("+", " ") || "",
    };

    //this creditCardId is actually the zuoraAccountId
    if (userStore.isEstablishedAccount) {
      await setNewDefaultCC(event.detail.creditCardId);
    }

    buyFlowStore.updateCreditCard(event.detail.creditCardId);
    logEvent("editPaymentCreditCardSuccessHandler", componentName, event.detail);
    buyFlowStore.creditCards = (await loadCreditCards()) || [];
    changePageState(pageStates.reviewOrder);
  } catch (err) {
    logException(err as Error, componentName);
  }
}

async function editPaymentCreditCardFailureHandler(error) {
  logEvent("editPaymentCreditCardFailureHandler", componentName, error);
  notificationsStore.clearNotifications();
  notificationsStore.addNotification({ type: error as NotificationType });
}

async function initiatePayPalRequest() {
  logEvent("Initiating paypal transaction", componentName);
  try {
    const paypalAgreementTokenResponse: IPayPalAgreementTokenResponse = (await unifiedApi.createPayPalAgreementToken())
      .data;
    logEvent("Got the paypal agreement token, redirecting to paypal approval url", componentName);
    sessionStorage.setItem("PayPal_token_id", paypalAgreementTokenResponse.tokenId);
    window.location.href = paypalAgreementTokenResponse.approvalUrl;
  } catch (error) {
    handleApiError(error as AxiosError, true);
  }
}

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

function setPaymentType(type: paymentTypes) {
  paymentType.value = type;
}

function cancel() {
  buyFlowStore.shippingAddress = Object.assign({}, buyFlowStore.editShippingAddress);
  buyFlowStore.layoutState = layoutStates.purchase;
}
async function submit() {
  buyFlowStore.processingOrderMessage = tn("processingOrderMessage");
  logEvent("submit", componentName, "Submit Edit Payment Credit Card Form");
  /* eslint-disable */
  //@ts-ignore
  Z.submit();
  /* eslint-enable */
}

async function loadPage() {
  isLoading.value = true;
  if (!userStore.isEstablishedAccount) {
    editPaymentDisplayState.value = editPaymentDisplayStates.addCreditCard;
  }
  isLoading.value = false;
}
</script>
